import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Layout from '../../../../components/Layout/Layout'
import { saveAs } from 'file-saver'

const AdminStoreRoomMismatchQty = () => {
  const navigate = useNavigate()

  const { id } = useParams()
  const [mismatchData, setMismatchData] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  // Every row the filter matches, not just the pages loaded so far.
  const [totalRecords, setTotalRecords] = useState(0)

  const observer = useRef()

  // Reset page to 1 when filterQuery changes and fetch data
  useEffect(() => {
    if (filterQuery.length > 2 || filterQuery === '') {
      setPage(1) // Reset to first page when search is applied
    }
  }, [filterQuery])

  // Fetch data when page or filterQuery changes
  useEffect(() => {
    const shouldFetch = filterQuery.length > 2 || filterQuery === ''
    if (shouldFetch) {
      fetchMismatchData(page, filterQuery)
    }
  }, [page, filterQuery])

  const fetchMismatchData = async (newPage = 1, search = '') => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/admin/stock-check-store/mismatch-qty/${id}`,
        {
          params: { page: newPage, limit: 30, search }, // Pass search parameter
        }
      )
      const { data, totalPages, totalRecords: records } = response.data
      setTotalRecords(records || 0)
      if (newPage === 1) {
        // If it's the first page, replace the entire array
        setMismatchData(data)
      } else {
        // Avoid duplicates by checking for existing _id
        setMismatchData((prev) => {
          const newData = data.filter(
            (item) => !prev.some((prevItem) => prevItem._id === item._id)
          )
          return [...prev, ...newData]
        })
      }
      setHasMore(newPage < totalPages)
    } catch (error) {
      toast.error('Failed to load mismatch data')
    } finally {
      setIsLoading(false)
    }
  }

  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [isLoading, hasMore]
  )

  const handleFilterChange = (value) => {
    setFilterQuery(value)
  }

  const exportFile = async () => {
    try {
      const response = await axios.get(
        `/api/v1/admin/stock-check-store/export-mismatch/${id}`,
        { responseType: 'blob' }
      )

      const contentDisposition = response.headers['content-disposition']
      let filename = 'download.xlsx'
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch.length === 2) {
          filename = filenameMatch[1]
        }
      }

      saveAs(response.data, filename)
    } catch (error) {
      console.error('Failed to download the file:', error)
      toast.error('Failed to download the file.')
    }
  }

  function formatDate(dateString) {
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  }

  return (
    <Layout title='MW-102 Mismatch Quantity'>
      <div
        className='shadow-sm p-3 bg-white rounded'
        style={{ width: '100%', position: 'fixed', height: '70px' }}
      >
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='d-flex'>
            <input
              type='text'
              className='form-control'
              style={{ width: '250px' }}
              placeholder='Search Item Code or Desc'
              onChange={(e) => handleFilterChange(e.target.value)}
              value={filterQuery}
            />
            <button
              className='btn btn-secondary btn-sm'
              onClick={() => setFilterQuery('')}
            >
              Clear
            </button>
          </div>

          <div
            className='fw-bold text-center flex-grow-1'
            style={{ fontSize: '18px' }}
          >
            MW-102 Quantity Mismatch / User Remarks
            <span className='fw-normal ms-2' style={{ fontSize: '15px' }}>
              ({totalRecords.toLocaleString()} records)
            </span>
          </div>

          <div className='d-flex gap-3'>
            <button
              type='button'
              className='btn btn-success btn-sm'
              onClick={() => exportFile()}
            >
              Export Data
            </button>

            <button
              type='button'
              className='btn btn-secondary btn-sm'
              onClick={() => navigate(`/admin/stock-check/rack-box/${id}`)}
            >
              Back to MW-102 List
            </button>

            <button
              type='button'
              className='btn btn-primary btn-sm'
              onClick={() => window.scrollTo(0, 0)}
            >
              <i className='bi bi-arrow-up-circle'></i>
            </button>
          </div>
        </div>
      </div>

      <div>
        {mismatchData.length === 0 && !isLoading && (
          <p>No mismatch data available.</p>
        )}
        <div className='table-responsive'>
          <table className='table' style={{ marginTop: '90px' }}>
            <thead>
              <tr>
                <th>Rack/Box</th>
                <th>Item Code/Desc</th>
                <th className='text-center'>Qty</th>
                <th className='text-center'>UOMRate</th>
                <th className='text-center'>Total</th>
                <th className='text-center text-nowrap' title='Physical Qty'>
                  Phy Qty
                </th>
                <th>Info</th>
                <th>User Remark</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {mismatchData.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan='11' style={{ textAlign: 'center' }}>
                    No mismatch data available.
                  </td>
                </tr>
              ) : (
                mismatchData.map((item, index) => (
                  <tr key={item._id}>
                    <td>
                      {item.rackNo} <br /> {item.boxNo}
                    </td>
                    <td>
                      {item.itemCode}
                      <br /> {item.description}
                    </td>
                    <td className='text-center'>{item.quantity}</td>
                    <td className='text-center'>{item.uomRate}</td>
                    <td className='text-center'>
                      {item.quantity * item.uomRate}
                    </td>
                    <td className='text-center'>{item.checkQty}</td>
                    <td>{item.remark || ''}</td>
                    <td>{item.userRemark}</td>
                    <td>
                      {item.lastUptByCode && (
                        <>
                          {item.lastUptByCode}
                          <br />
                          {item.lastUptDate ? formatDate(item.lastUptDate) : ''}
                        </>
                      )}
                    </td>
                    {/* Attach the ref to the last item */}
                    {index === mismatchData.length - 1 && (
                      <td ref={lastElementRef}></td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {isLoading && <p>Loading more data...</p>}
      </div>
    </Layout>
  )
}

export default AdminStoreRoomMismatchQty
